import {
  BooleanFilter,
  EnumFilter,
  SelectFilter,
  WbSearch,
  WbTable,
  WbTableFilters,
} from '@agilelab/plugin-wb-platform';
import { Button, Checkbox, Radio, Tooltip, useTheme } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { CSSProperties, useMemo, useState } from 'react';
import {
  ActionsContextProvider,
  DataRootPathContextProvider,
  useAction,
  useDataPath,
} from '../../context';
import { CustomViewRegister } from '../CustomView';

import {
  parseNunjucks,
  snakeCaseToTitleCase,
} from '@agilelab/plugin-wb-platform-common';
import _, { isUndefined } from 'lodash';
import { getChildren } from '../CustomView';
import { LinkIcon } from '../Icons';
import { WbStatusBadge } from '../WbStatusBadge';

const processShowWhenRow = (
  showRowWhen: { value?: string; equals?: string; notEquals?: string },
  item: Record<string, any>,
): Record<string, any> | null => {
  if (showRowWhen?.value) {
    const parsedValue =
      parseNunjucks(showRowWhen.value, item, true) ?? showRowWhen.value;
    const parsedEqual = showRowWhen.equals;
    const parsedNotEqual = showRowWhen.notEquals;

    if (parsedEqual !== undefined && parsedValue !== parsedEqual?.toString())
      return null;
    if (
      parsedNotEqual !== undefined &&
      parsedValue === parsedNotEqual?.toString()
    )
      return null;
  }
  return item;
};
const explodeTreeView = (
  list: Record<string, any>[],
  key: string,
  level: number = 0,
  path: number[] = [],
) =>
  list.flatMap((elem: any, i: number) => {
    let result = [
      Object.assign(
        {
          _treeview: {
            level,
            hasChildren: key in elem,
            id: [path.length, i].join('/'),
            path: path.concat([i]).map((p, k) => [k, p].join('/')),
          },
        },
        elem,
      ),
    ];
    if (key in elem)
      result = result.concat(
        explodeTreeView(elem[key], key, level + 1, path.concat([i])),
      );
    return result;
  });

CustomViewRegister.register({
  id: 'table',
  function: function TableView(props: Record<string, any>) {
    const hideEmptyColumns = props.hideEmptyColumns || false;
    const clickFn = useAction(props.click);
    const [closedNodes, setClosedNodes] = useState<string[]>([]);
    const parent = useDataPath('');
    let list: any[] = useDataPath(props.path) ?? [];

    list = list.map((elem: any) =>
      Object.assign({}, elem, { _parent: parent }),
    );
    const showRowWhen = props.showRowWhen;

    const filteredList = list
      .map(row => processShowWhenRow(showRowWhen, row))
      .filter(
        (row: any) =>
          !closedNodes.some((closedNode: string) => {
            return row._treeview.path.slice(0, -1).indexOf(closedNode) >= 0;
          }),
      );
    const onClick = (ev: any, rowData: any, idx: number) => {
      const tag = (ev.target as HTMLElement).tagName ?? '';
      const tagParent = (ev.target as HTMLElement).parentElement?.tagName ?? '';
      // bypass if user clicks on a link
      if (tag !== 'A' && tagParent !== 'A') clickFn(rowData, idx);
    };

    const columns = getChildren(props)
      .map(elem => {
        const defaultSortable = ['string'].includes(elem.props.type);

        return {
          elem,
          width: elem.props.width || undefined,
          minWidth: elem.props.minWidth || undefined,
          maxWidth: elem.props.maxWidth || undefined,
          resizable: elem.props.resizable ?? true,
          title: elem.props.label || elem.props.title,
          path: elem.props.path,
          align: elem.props.align || '',
          valueRef: !!elem.props.value
            ? elem.props.value
            : ['{{', elem.props.path, '}}'].join(' '),
          showWhenExistsPath: elem.props.showWhenExistsPath || '',
          sortable:
            ['string'].includes(elem.props.type) &&
            !isUndefined(elem.props.sortable)
              ? elem.props.sortable
              : defaultSortable,
        };
      })
      .filter(elem => {
        if (hideEmptyColumns) {
          if (!filteredList.some(row => !!parseNunjucks(elem.valueRef, row)))
            return false;
        }
        if ((elem.showWhenExistsPath || '') === '') return true;
        return !!_.get(
          (list as Record<string, any>)._parent,
          elem.showWhenExistsPath,
        );
      });

    const rowStyle: CSSProperties = {};
    if (props.click) rowStyle.cursor = 'pointer';

    const filters = props.filters || [];

    const createInitialFiltersState = () => {
      const initialFiltersState: Record<string, any> = {};

      if (filters.length === 0) return initialFiltersState;

      const paths = columns.reduce((result, col) => {
        result[col.title] = col.elem.props.path;
        return result;
      }, {} as Record<string, string>);

      filters.forEach((filter: any) => {
        if (!paths[filter.column] && !filter.path) {
          return new Promise(() => {
            throw new Error(
              `Filter column ${filter.column} not found in columns. Please check your filters configuration.`,
            );
          });
        }

        const initialFilterValues: Record<string, any> = {
          multiselect: [],
          boolean: false,
        };

        initialFiltersState[filter.column] = {
          value: initialFilterValues[filter.type] || '',
          path: filter.path || paths[filter.column],
          type: filter.type,
          column: filter.column,
          ...filter,
        };

        return undefined;
      });
      return initialFiltersState;
    };

    const [filtersState, setFiltersState] = useState(
      createInitialFiltersState(),
    );

    const filteredData = useMemo(() => {
      const filterRow = (
        row: any,
        filterStates: [string, { type: string; value: any; path: string }][],
      ) => {
        return filterStates.every(([__, { type, value, path }]) => {
          const rowValue = getNestedProperty(row, path);

          switch (type) {
            case 'multiselect':
              return !value?.length || value.includes(rowValue);

            case 'boolean':
              return !value || rowValue === value;

            case 'select':
            case 'search':
            default:
              return (
                !value || rowValue.toLowerCase().includes(value.toLowerCase())
              );
          }
        });
      };

      return filteredList.filter(row =>
        filterRow(row, Object.entries(filtersState)),
      );
    }, [filteredList, filtersState]);

    const handleSearchChange = (column: string, value: any) => {
      setFiltersState(prev => ({
        ...prev,
        [column]: {
          ...prev[column],
          value,
        },
      }));
    };

    function getNestedProperty(obj: any, path: string) {
      const segments = path.replace(/\[(['"\w]+)\]/g, '.$1').split('.');
      return segments.reduce((acc, part) => {
        if (acc && acc[part] !== undefined) {
          return acc[part];
        }
        return undefined;
      }, obj);
    }

    const resetFilters = () => {
      setFiltersState(createInitialFiltersState());
    };

    const renderFilters = () => {
      return Object.entries(filtersState).map(
        ([column, { type, value, path, placeholder }]) => {
          const uniqueOptions = new Set();

          filteredList.forEach(row => {
            const rowValue = getNestedProperty(row, path);
            if (
              rowValue !== null &&
              rowValue !== undefined &&
              typeof rowValue !== 'object'
            ) {
              uniqueOptions.add(rowValue);
            }
          });

          const options: any[] = [...uniqueOptions];

          switch (type) {
            case 'search':
              return (
                <WbSearch
                  key={column}
                  value={value}
                  style={{ maxWidth: '200px' }}
                  onChange={e => handleSearchChange(column, e)}
                  placeholder={placeholder || `Filter by ${column}`}
                />
              );
            case 'boolean':
              return (
                <BooleanFilter
                  key={column}
                  field={column}
                  value={value}
                  onChange={e => handleSearchChange(column, e)}
                />
              );
            case 'multiselect':
              return (
                <EnumFilter<string>
                  key={column}
                  field={column}
                  renderOption={o => snakeCaseToTitleCase(o)}
                  renderValue={v => snakeCaseToTitleCase(v)}
                  value={value}
                  onChange={e => handleSearchChange(column, e)}
                  onSearch={v =>
                    options.filter(o => new RegExp(v, 'ig').test(o))
                  }
                  options={options}
                />
              );
            case 'select':
            default:
              return (
                <SelectFilter<string>
                  key={column}
                  field={column}
                  renderOption={o => snakeCaseToTitleCase(o)}
                  renderValue={v => snakeCaseToTitleCase(v)}
                  value={value}
                  onChange={e => handleSearchChange(column, e)}
                  onSearch={v =>
                    options.filter(o => new RegExp(v, 'ig').test(o))
                  }
                  options={options}
                />
              );
          }
        },
      );
    };

    const wbTableColumns = columns.map(elem => {
      const path =
        elem.path ?? elem.valueRef.replace('{{', '').replace('}}', '').trim();

      return {
        field: path ?? elem.title,
        width: elem.width,
        minWidth: elem.minWidth,
        maxWidth: elem.maxWidth,
        resizable: elem.resizable,
        sortable: elem.sortable,
        headerName: elem.title,
        fieldRender: () => {
          return elem.elem;
        },
      };
    });

    return (
      <ActionsContextProvider actions={{ getMode: () => 'table' }}>
        {!!filters.length && (
          <WbTableFilters onClear={resetFilters}>
            {renderFilters()}
          </WbTableFilters>
        )}
        <WbTable
          components={{
            tableContent: {
              columns: wbTableColumns,
              rows: filteredData,
              rowsWrapper: DataRootPathContextProvider,
              onRowClick: (element, event, idx) => {
                onClick(event, element, idx);
              },
              treeProps: {
                expandedRowsIds: closedNodes,
                getRowChildren: r => r?._treeview ?? [],
                onExpandChange: newExpanded => setClosedNodes(newExpanded),
              },
            },
          }}
        />
      </ActionsContextProvider>
    );
  },
});

CustomViewRegister.register({
  id: 'radio',
  function: function RadioButton(props: Record<string, any>) {
    const action = useAction(props.click);
    const data = useDataPath('');
    const nunjucksDisabled = parseNunjucks(props.disabled, data, true);
    const disabled =
      nunjucksDisabled !== undefined ? !!JSON.parse(nunjucksDisabled) : false;

    return (
      <Tooltip
        title={disabled ? props.disabledTooltip ?? '' : props.tooltip ?? ''}
      >
        <span>
          <Radio
            onClick={e => {
              e.stopPropagation();
              action(data);
            }}
            checked={Boolean(props.value)}
            color="primary"
            disabled={disabled}
          />
        </span>
      </Tooltip>
    );
  },
});

CustomViewRegister.register({
  id: 'checkbox',
  function: function RadioButton(props: Record<string, any>) {
    const action = useAction(props.click);
    const data = useDataPath('');
    const nunjucksDisabled = parseNunjucks(props.disabled, data, true);
    const disabled =
      nunjucksDisabled !== undefined ? !!JSON.parse(nunjucksDisabled) : false;

    return (
      <Tooltip
        title={disabled ? props.disabledTooltip ?? '' : props.tooltip ?? ''}
      >
        <span>
          <Checkbox
            onClick={e => {
              e.stopPropagation();
              action(data);
            }}
            checked={Boolean(props.value)}
            color="primary"
            disabled={disabled}
          />
        </span>
      </Tooltip>
    );
  },
});

CustomViewRegister.register({
  id: 'enabled',
  function: function RadioButton(props: Record<string, any>) {
    const theme = useTheme();
    const color = props.value
      ? theme.palette.success.main
      : theme.palette.grey[300];
    return <CheckCircleIcon htmlColor={color} />;
  },
});

CustomViewRegister.register({
  id: 'status',
  function: function Status(props: Record<string, any>) {
    // if the value is not defined we should show nothing instead of KO
    if (props.value === undefined) return <></>;
    return <WbStatusBadge ok={props.value} />;
  },
});

CustomViewRegister.register({
  id: 'row_link',
  function: function RowLink(props: Record<string, any>) {
    const click = useAction(props.click);
    const rowData = useDataPath('');
    if (!props.value) return <></>;
    return (
      <Button
        onClick={() => click(rowData)}
        aria-label={props.label}
        title={props.label}
        size="small"
      >
        <LinkIcon fontSize="small" />
      </Button>
    );
  },
});
